import { getLdsBoardBasisLabelHebrew } from "../../utils/ldsBoardBasisHebrew";
import { resolveLdsSuccessDisplayOrderCode } from "../../utils/resolveLdsSuccessDisplayOrderCode";
import { formatHrLabel } from "../../utils/resolveCustomerFacingOfferNumber";
import { getDepRetFlightPairs } from "../../utils/vacationPackagesLinkedFlights";
import { resolvePackageFlightDisplayLinkedFlights } from "../../utils/vacationPackageFlightSelectionSSOT";
import { packageLegToFlightLegBlockLeg } from "../../Components/Common/UnifiedPackageFlightLeg/packageFlightLegAdapter";
import {
  deriveLdsOrderBusinessStatus,
  isBankTransferAwaitingPayment,
  isPaidOrder,
  LDS_ORDER_BUSINESS_STATUS,
} from "../../utils/ldsOrderStatus";
import {
  LDS_DOMESTIC_BANK_TRANSFER,
  LDS_INTERNATIONAL_BANK_TRANSFER,
} from "../../utils/ldsBankTransferDetails";
import { PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER } from "../../utils/packageProfileCustomerPayment";
import { ilsToAgorot } from "../../utils/publicMoneyAgorot";
import {
  BANK_PAID_NEXT_STEPS,
  BANK_PENDING_NEXT_STEPS,
  CARD_NEXT_STEPS,
  PROCESSING_NEXT_STEPS,
  PUBLIC_BOOKING_COPY as C,
} from "./publicBookingCopy";

function trimText(value) {
  if (value == null) return "";
  return String(value).trim();
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    const t = trimText(v);
    if (t) return t;
  }
  return "";
}

function looksLikeSupplierRoomCode(name) {
  const t = trimText(name);
  if (!t) return true;
  if (/roomset/i.test(t)) return true;
  if (/^[A-Z0-9_\-]{8,}$/.test(t) && !/\s/.test(t)) return true;
  return false;
}

function formatStayDate(raw) {
  const text = trimText(raw);
  if (!text) return "";
  const iso = text.includes("T") ? text.split("T", 1)[0] : text;
  const mIso = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (mIso) return `${mIso[3]}/${mIso[2]}/${mIso[1]}`;
  const mDmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mDmy) {
    return `${String(mDmy[1]).padStart(2, "0")}/${String(mDmy[2]).padStart(2, "0")}/${mDmy[3]}`;
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const d = raw.getDate().toString().padStart(2, "0");
    const mo = (raw.getMonth() + 1).toString().padStart(2, "0");
    return `${d}/${mo}/${raw.getFullYear()}`;
  }
  return text;
}

function parseDateParts(raw) {
  const text = trimText(raw);
  if (!text) return null;
  const iso = text.includes("T") ? text.split("T", 1)[0] : text;
  const mIso = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (mIso) return { y: Number(mIso[1]), m: Number(mIso[2]), d: Number(mIso[3]) };
  const mDmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mDmy) return { y: Number(mDmy[3]), m: Number(mDmy[2]), d: Number(mDmy[1]) };
  return null;
}

function nightsBetween(checkIn, checkOut, fallbackNights) {
  const n = Number(fallbackNights);
  if (Number.isFinite(n) && n > 0) return Math.round(n);
  const a = parseDateParts(checkIn);
  const b = parseDateParts(checkOut);
  if (!a || !b) return null;
  const ms = Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d);
  const nights = Math.round(ms / 86400000);
  return nights > 0 ? nights : null;
}

function formatIls(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `₪${Math.round(n).toLocaleString("he-IL")}`;
}

function personName(p) {
  if (!p || typeof p !== "object") return "";
  return firstNonEmpty(
    [p.firstname || p.firstName, p.lastname || p.lastName].filter(Boolean).join(" "),
    p.fullname || p.fullName || p.name
  );
}

function collectTravelers(roomsAndPaxInfo) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const names = [];
  for (const slot of rooms) {
    if (!slot || typeof slot !== "object") continue;
    for (const key of ["adults", "kids", "children", "infants"]) {
      const list = Array.isArray(slot[key]) ? slot[key] : [];
      for (const p of list) {
        const name = personName(p);
        if (name) names.push({ fullName: name });
      }
    }
  }
  return names;
}

function occupancyLine(slot) {
  const adults = Array.isArray(slot?.adults) ? slot.adults.length : Number(slot?.adults) || 0;
  const kids = Array.isArray(slot?.kids)
    ? slot.kids.length
    : Array.isArray(slot?.children)
      ? slot.children.length
      : Number(slot?.kids) || 0;
  const infants = Array.isArray(slot?.infants) ? slot.infants.length : Number(slot?.infants) || 0;
  const parts = [];
  if (adults) parts.push(`${adults} מבוגרים`);
  if (kids) parts.push(`${kids} ילדים`);
  if (infants) parts.push(`${infants} תינוקות`);
  return parts.join(", ");
}

function roomNameFromSlot(slot, selectedResult, index) {
  const name = firstNonEmpty(
    slot?.room?.roomType,
    slot?.room?.RoomType,
    slot?.room?.name,
    slot?.roomType,
    selectedResult?.selectedRoom?.roomType,
    selectedResult?.hotel?.roomType,
    selectedResult?.fullDeal?.hotel?.roomType,
    selectedResult?.public_selected_room_snapshot?.roomType,
    selectedResult?.public_selected_room_snapshot?.name
  );
  if (!name || looksLikeSupplierRoomCode(name)) return `חדר ${index + 1}`;
  return name;
}

function boardFromSlot(slot, selectedResult, fallbackBoardLabel) {
  const raw =
    slot?.room?.board ||
    slot?.room?.boardBasis ||
    slot?.board ||
    selectedResult?.hotel?.board ||
    selectedResult?.fullDeal?.hotel?.board;
  const labeled = raw ? getLdsBoardBasisLabelHebrew(raw) : "";
  return firstNonEmpty(labeled, fallbackBoardLabel, raw);
}

function mapFlightLeg(leg, titleHe) {
  if (!leg) return null;
  const mappedWrap = packageLegToFlightLegBlockLeg(leg, { labelHe: titleHe, airlines: [] });
  const mapped = mappedWrap?.mapped;
  if (!mapped) return null;
  const from = trimText(mapped.originCode);
  const to = trimText(mapped.destinationCode);
  if (!from || !to || from === "—" || to === "—") return null;
  return {
    titleHe,
    from,
    to,
    depTime: mapped.departureTime && mapped.departureTime !== "—" ? mapped.departureTime : "",
    arrTime: mapped.arrivalTime && mapped.arrivalTime !== "—" ? mapped.arrivalTime : "",
    dateLabel: mapped.depDateDisplay || mapped.depDate || "",
    flightNumber: mapped.flightNumber || "",
    airlineCode: mapped.airlineCode || "",
  };
}

function extractFlights(selectedResult, order) {
  const linked =
    resolvePackageFlightDisplayLinkedFlights(selectedResult).linkedFlights ||
    order?.linkedflights ||
    order?.linkedFlights ||
    null;
  const pairs = getDepRetFlightPairs(linked);
  return {
    outbound: mapFlightLeg(pairs.dep?.leg, C.outbound),
    inbound: mapFlightLeg(pairs.ret?.leg, C.inbound),
  };
}

function extractBaggageItems(ldsSupplement, apiBaggageSummary) {
  const extras = [];
  const list = Array.isArray(ldsSupplement) ? ldsSupplement : [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const type = String(item.type || item.supplementType || "").toUpperCase();
    const label = firstNonEmpty(item.name, item.title, item.description, item.supplementName);
    if (!label) continue;
    const low = label.toLowerCase();
    const isBag =
      /LUGGAGE|BAGGAGE|TROLLEY/.test(type) ||
      /baggage|luggage|מזווד|כבודה|מטען|trolley|cabin/.test(low);
    if (isBag) extras.push(label);
  }
  if (extras.length) return extras;
  const summary = trimText(apiBaggageSummary);
  if (summary && summary !== "ללא תוספת כבודה שמורה") return [summary];
  return [];
}

function pickPublicToken(order) {
  return firstNonEmpty(
    order?.publicConfirmationToken,
    order?.public_confirmation_token,
    order?.public_access_token,
    order?.publicAccessToken
  );
}

function pickPayableIls({ payableIls, order, apiAmountIls }) {
  const candidates = [
    payableIls,
    apiAmountIls,
    order?.ourprice?.display_total_ils,
    order?.ourprice?.public_final_customer_price_ils,
    order?.ourPrice?.display_total_ils,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function apiHasMoneyAmounts(api) {
  if (!api || typeof api !== "object") return false;
  return ilsToAgorot(api.amountIls) != null && ilsToAgorot(api.amountPaidIls) != null;
}

function moneySaysFullyPaid(api) {
  if (!api || typeof api !== "object") return false;
  const payableA = ilsToAgorot(api.amountIls);
  const paidA = ilsToAgorot(api.amountPaidIls);
  if (payableA == null || paidA == null) return false;
  return paidA >= payableA && payableA > 0;
}

function resolvePaidFromBackend({ api, order, isBankMethod }) {
  if (apiHasMoneyAmounts(api)) {
    return moneySaysFullyPaid(api);
  }
  if (order) {
    return isPaidOrder(order);
  }
  if (isBankMethod) {
    return api?.paid === true && api?.isPartial !== true;
  }
  return false;
}

function resolveVariant({ paymentMethod, order, apiConfirmation }) {
  const api = apiConfirmation && typeof apiConfirmation === "object" ? apiConfirmation : null;
  const method = String(paymentMethod || api?.paymentMethod || "").toLowerCase();
  const isBankMethod =
    method === PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER ||
    method === "bank_transfer" ||
    method === "banktransfer" ||
    api?.bankTransfer === true ||
    isBankTransferAwaitingPayment(order);
  // Card/split: persisted money (amountPaidIls vs amountIls, agorot) or order ledger.
  // Never a HYP redirect, snapshot, or paid boolean without amounts.
  const paid = resolvePaidFromBackend({ api, order, isBankMethod });
  const status = order ? deriveLdsOrderBusinessStatus(order) : null;
  const ppc = trimText(
    order?.post_payment_checkout_status || api?.checkoutStatus
  ).toLowerCase();
  const apiPartial =
    api?.isPartial === true ||
    ((ilsToAgorot(api?.amountPaidIls) || 0) > 0 && !paid);

  if (ppc === "payment_authorized_order_failed" || ppc === "authorized_pending_booking") {
    return "processing";
  }
  if (status === LDS_ORDER_BUSINESS_STATUS.PARTIAL_PAYMENT || apiPartial) return "partial";
  if (isBankMethod && paid) return "bank_paid";
  if (isBankMethod && !paid) return "bank_pending";
  if (paid) return "card_paid";
  if (isBankMethod) return "bank_pending";
  return "awaiting_payment";
}

function heroForVariant(variant) {
  switch (variant) {
    case "bank_pending":
      return { title: C.bankPendingTitle, lead: C.bankPendingLead, tone: "pending" };
    case "bank_paid":
      return { title: C.bankPaidTitle, lead: C.bankPaidLead, tone: "success" };
    case "processing":
      return { title: C.processingTitle, lead: C.processingLead, tone: "warning" };
    case "partial":
      return { title: C.partialTitle, lead: C.partialLead, tone: "warning" };
    case "awaiting_payment":
      return { title: C.awaitingTitle, lead: C.awaitingLead, tone: "pending" };
    case "card_paid":
      return { title: C.cardPaidTitle, lead: C.cardPaidLead, tone: "success" };
    default:
      return { title: C.awaitingTitle, lead: C.awaitingLead, tone: "pending" };
  }
}

function nextStepsForVariant(variant) {
  if (variant === "bank_pending") return BANK_PENDING_NEXT_STEPS;
  if (variant === "bank_paid") return BANK_PAID_NEXT_STEPS;
  if (variant === "processing") return PROCESSING_NEXT_STEPS;
  return CARD_NEXT_STEPS;
}

function paymentLabels(variant, isBank) {
  if (variant === "bank_pending") {
    return { statusLabel: C.pendingStatus, methodLabel: C.methodBank, totalLabel: C.totalLabel };
  }
  if (variant === "bank_paid") {
    return { statusLabel: C.receivedStatus, methodLabel: C.methodBank, totalLabel: C.paidTotalLabel };
  }
  if (variant === "processing") {
    return {
      statusLabel: C.receivedStatus,
      methodLabel: isBank ? C.methodBank : C.methodCard,
      totalLabel: C.totalLabel,
    };
  }
  if (variant === "partial") {
    return {
      statusLabel: C.partialStatus,
      methodLabel: isBank ? C.methodBank : C.methodCard,
      totalLabel: C.totalLabel,
    };
  }
  if (variant === "awaiting_payment") {
    return { statusLabel: C.pendingStatus, methodLabel: C.methodCard, totalLabel: C.totalLabel };
  }
  if (variant === "card_paid") {
    return { statusLabel: C.paidStatus, methodLabel: C.methodCard, totalLabel: C.paidTotalLabel };
  }
  return { statusLabel: C.pendingStatus, methodLabel: C.methodCard, totalLabel: C.totalLabel };
}

/**
 * Customer-facing confirmation VM. Prefers checkout SSOT + real HR order number.
 */
export function buildPublicBookingConfirmationVm({
  order = null,
  selectedResult = null,
  roomsAndPaxInfo = null,
  payableIls = null,
  paymentMethod = null,
  nights = null,
  destination = "",
  boardLabel = "",
  checkIn = "",
  checkOut = "",
  heroImageUrl = "",
  apiConfirmation = null,
  checkoutStatus: _checkoutStatus = "",
} = {}) {
  const api = apiConfirmation && typeof apiConfirmation === "object" ? apiConfirmation : null;
  const orderRef =
    firstNonEmpty(
      api?.hrOrderNumber,
      formatHrLabel(order?.booking_ref),
      formatHrLabel(order?.hr_order_number),
      resolveLdsSuccessDisplayOrderCode(order, order?.id)?.selectedDisplayCode
    ) || "";

  const technicalOrderId = firstNonEmpty(order?.id, order?.orderid, api?.technicalOrderId);
  const publicToken = firstNonEmpty(pickPublicToken(order), api?.publicToken);

  const variant = resolveVariant({
    paymentMethod: paymentMethod || api?.paymentMethod,
    order,
    apiConfirmation: api,
  });
  const isBank = variant === "bank_pending" || variant === "bank_paid";
  const hero = heroForVariant(variant);
  const payLabels = paymentLabels(variant, isBank);

  const roomsSrc = Array.isArray(roomsAndPaxInfo)
    ? roomsAndPaxInfo
    : Array.isArray(order?.passengersinfo)
      ? order.passengersinfo
      : Array.isArray(order?.passengers_info)
        ? order.passengers_info
        : [];

  const rooms = roomsSrc.length
    ? roomsSrc.map((slot, index) => ({
        name: roomNameFromSlot(slot, selectedResult, index),
        board: boardFromSlot(slot, selectedResult, firstNonEmpty(boardLabel, api?.board)),
        occupancy: occupancyLine(slot),
      }))
    : Array.isArray(api?.rooms)
      ? api.rooms
      : [];

  const travelers = collectTravelers(roomsSrc).length
    ? collectTravelers(roomsSrc)
    : Array.isArray(api?.travelers)
      ? api.travelers
      : [];
  const flights = extractFlights(selectedResult, order);
  if (!flights.outbound && Array.isArray(api?.flights) && api.flights[0]) {
    flights.outbound = api.flights[0];
  }
  if (!flights.inbound && Array.isArray(api?.flights) && api.flights[1]) {
    flights.inbound = api.flights[1];
  }

  const hotelName = firstNonEmpty(
    selectedResult?.hotel?.hotelName,
    selectedResult?.fullDeal?.hotel?.hotelName,
    selectedResult?.packageName,
    api?.hotelName
  );
  const dest = firstNonEmpty(destination, api?.destination);
  const inDate = firstNonEmpty(formatStayDate(checkIn), api?.checkIn);
  const outDate = firstNonEmpty(formatStayDate(checkOut), api?.checkOut);
  const nightCount = nightsBetween(
    checkIn || inDate,
    checkOut || outDate,
    nights ?? api?.nights
  );

  const amount = pickPayableIls({
    payableIls: api?.amountIls ?? payableIls,
    order,
    apiAmountIls: api?.amountIls,
  });
  const paidIls = (() => {
    const n = Number(api?.amountPaidIls);
    if (Number.isFinite(n) && n >= 0) return n;
    return null;
  })();
  const dueIls = (() => {
    const n = Number(api?.amountDueIls);
    if (Number.isFinite(n) && n >= 0) return n;
    return null;
  })();

  const baggageItems = extractBaggageItems(
    order?.supplement || selectedResult?.supplement,
    api?.baggageSummary
  );

  const bankFromApi = api?.bankTransferInstructions;
  const bankTransfer =
    variant === "bank_pending"
      ? {
          amountDue: amount,
          amountDisplay: formatIls(amount) || api?.amountDisplay || "",
          referenceHint: orderRef,
          domestic: bankFromApi?.domestic || LDS_DOMESTIC_BANK_TRANSFER,
          international: bankFromApi?.international || LDS_INTERNATIONAL_BANK_TRANSFER,
          deadline: bankFromApi?.deadline || null,
        }
      : null;

  const purchaser = roomsSrc[0]?.adults?.[0] || {};

  return {
    orderRef,
    technicalOrderId,
    publicToken,
    variant,
    hero,
    emailNote: variant === "card_paid" ? C.cardPaidEmail : "",
    nextSteps: nextStepsForVariant(variant),
    hotelName,
    destination: dest,
    checkIn: inDate,
    checkOut: outDate,
    nights: nightCount,
    board: firstNonEmpty(boardLabel, rooms[0]?.board, api?.board),
    heroImageUrl: firstNonEmpty(
      heroImageUrl,
      selectedResult?.hotel?.resourceUrl,
      api?.heroImageUrl
    ),
    travelers,
    rooms,
    flights,
    baggageItems,
    payment: {
      method: isBank ? "bank_transfer" : "card",
      ...payLabels,
      totalIls: amount,
      totalDisplay: formatIls(amount) || api?.amountDisplay || "",
      paidIls,
      paidDisplay: paidIls != null && paidIls > 0 ? formatIls(paidIls) : "",
      dueIls,
      dueDisplay: dueIls != null && dueIls > 0 ? formatIls(dueIls) : "",
      authoritative: Boolean(api),
    },
    bankTransfer,
    purchaserLastName: trimText(purchaser.lastname || purchaser.lastName),
    supportEmail: C.supportEmail,
    chatHref: "/customer-chat/start",
  };
}

function sanitizeSnapshotVariant(variant) {
  const v = String(variant || "");
  if (v === "card_paid" || v === "bank_paid") return "awaiting_payment";
  if (
    v === "bank_pending" ||
    v === "partial" ||
    v === "processing" ||
    v === "awaiting_payment"
  ) {
    return v;
  }
  return "awaiting_payment";
}

export function mergeConfirmationSources(snapshotVm, apiConfirmation, order) {
  const base = snapshotVm && typeof snapshotVm === "object" ? snapshotVm : {};
  const apiPresent = Boolean(apiConfirmation) || Boolean(order);
  const fromApi = buildPublicBookingConfirmationVm({
    order,
    apiConfirmation,
    paymentMethod: apiPresent ? apiConfirmation?.paymentMethod : base.payment?.method,
    payableIls: apiPresent ? apiConfirmation?.amountIls : null,
    roomsAndPaxInfo: null,
    selectedResult: null,
  });
  const variant = apiPresent ? fromApi.variant : sanitizeSnapshotVariant(base.variant);
  const snapshotHero =
    variant === sanitizeSnapshotVariant(base.variant) && variant === base.variant
      ? base.hero
      : heroForVariant(variant);
  return {
    ...fromApi,
    orderRef: firstNonEmpty(fromApi.orderRef, base.orderRef),
    publicToken: firstNonEmpty(fromApi.publicToken, base.publicToken),
    technicalOrderId: firstNonEmpty(fromApi.technicalOrderId, base.technicalOrderId),
    variant,
    hero: apiPresent ? fromApi.hero : snapshotHero || heroForVariant(variant),
    nextSteps: apiPresent ? fromApi.nextSteps : nextStepsForVariant(variant),
    emailNote: apiPresent ? fromApi.emailNote : "",
    payment: apiPresent
      ? fromApi.payment
      : {
          ...(base.payment || {}),
          ...paymentLabels(variant, variant === "bank_pending" || variant === "bank_paid"),
          paidIls: null,
          paidDisplay: "",
          dueIls: variant === "bank_pending" ? base.payment?.dueIls ?? null : null,
          dueDisplay: variant === "bank_pending" ? base.payment?.dueDisplay || "" : "",
          authoritative: false,
        },
    bankTransfer:
      variant === "bank_pending"
        ? fromApi.bankTransfer || base.bankTransfer || null
        : apiPresent
          ? fromApi.bankTransfer
          : null,
    hotelName: firstNonEmpty(apiPresent ? fromApi.hotelName : "", base.hotelName, fromApi.hotelName),
    destination: firstNonEmpty(base.destination, fromApi.destination),
    checkIn: firstNonEmpty(base.checkIn, fromApi.checkIn),
    checkOut: firstNonEmpty(base.checkOut, fromApi.checkOut),
    nights: base.nights || fromApi.nights,
    board: firstNonEmpty(base.board, fromApi.board),
    travelers: apiPresent
      ? fromApi.travelers?.length
        ? fromApi.travelers
        : []
      : base.travelers || [],
    rooms: apiPresent ? (fromApi.rooms?.length ? fromApi.rooms : base.rooms || []) : base.rooms || fromApi.rooms,
    flights: {
      outbound: (apiPresent && fromApi.flights?.outbound) || base.flights?.outbound || fromApi.flights?.outbound || null,
      inbound: (apiPresent && fromApi.flights?.inbound) || base.flights?.inbound || fromApi.flights?.inbound || null,
    },
    baggageItems: apiPresent
      ? fromApi.baggageItems || []
      : base.baggageItems?.length
        ? base.baggageItems
        : fromApi.baggageItems,
    heroImageUrl: firstNonEmpty(base.heroImageUrl, fromApi.heroImageUrl),
  };
}
